import React, { useState, useEffect } from "react";
import { C, ROLE_PERMISSIONS } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, MeetingsTab, TransactionsTab,
  OperationalPlanTab, ReportsTab, useToast
} from "./erp-shared";
import {
  Briefcase, Users, UserPlus, Plus, Search, CheckCircle, Clock,
  AlertCircle, FileText, Calendar, Target, Star, Phone, Mail,
  MapPin, TrendingUp, Award, Building, Hash, Eye, Edit,
  DollarSign, CalendarOff, GraduationCap, Timer, Layers,
  UserCheck, UserX, CalendarDays, TreePine
} from "lucide-react";

/* ═════════════════════════════════════════════
   ✦ HR Page
   ═════════════════════════════════════════════ */
export default function HRPage({ user }) {
  const [tab, setTab] = useState("employees");
  const [triggers, setTriggers] = useState({ leave: 0 });
  const perms = ROLE_PERMISSIONS[user.role] || {};

  const tabs = [
    { key: "employees", label: "الموظفون", icon: Users },
    { key: "structure", label: "الهيكل التنظيمي", icon: Layers },
    { key: "leaves", label: "الإجازات", icon: TreePine },
    { key: "payroll", label: "الرواتب", icon: DollarSign },
    { key: "meetings", label: "الاجتماعات", icon: Calendar },
    { key: "transactions", label: "المعاملات المالية", icon: FileText },
    { key: "plan", label: "الخطة التشغيلية", icon: Target },
    { key: "reports", label: "التقارير", icon: FileText },
  ];

  function fireQuick(targetTab, triggerKey) {
    setTab(targetTab);
    setTriggers(prev => ({ ...prev, [triggerKey]: prev[triggerKey] + 1 }));
  }

  return (
    <div>
      <ModuleHeader icon={Briefcase} title="الموارد البشرية" subtitle="إدارة الموظفين والهيكل التنظيمي والإجازات والرواتب" />

      {perms.canEdit && (
        <div style={hq.row}>
          <button style={hq.btn("#059669")} onClick={() => fireQuick("leaves", "leave")}>
            <span style={hq.emoji}>🌴</span><span>طلب إجازة / إذن</span>
          </button>
        </div>
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "employees" && <EmployeesTab canEdit={perms.canEdit} />}
      {tab === "structure" && <StructureTab />}
      {tab === "leaves" && <LeavesTab canEdit={perms.canEdit} triggerOpen={triggers.leave} />}
      {tab === "payroll" && <PayrollTab />}
      {tab === "meetings" && <MeetingsTab departmentName="الموارد البشرية" />}
      {tab === "transactions" && <TransactionsTab budget={1200000} />}
      {tab === "plan" && <OperationalPlanTab departmentKey="hr" />}
      {tab === "reports" && <ReportsTab departmentName="الموارد البشرية" />}
    </div>
  );
}

const hq = {
  row: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 },
  btn: (color) => ({
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 16px", borderRadius: 10,
    background: `${color}10`, color, border: `1px solid ${color}30`,
    fontFamily: "'Readex Pro', sans-serif", fontSize: 13, fontWeight: 700,
    cursor: "pointer", transition: "all 0.15s",
  }),
  emoji: { fontSize: 16 },
};

/* ─────────────────────────────────────────────
   ✦ Employees Tab
   ───────────────────────────────────────────── */
const INITIAL_EMP = { name: "", empId: "", position: "", department: "", phone: "", email: "", salary: "", qualification: "", joinDate: "" };

function EmployeesTab({ canEdit }) {
  const [employees, setEmployees] = useState(DEMO_EMPLOYEES);
  const [search, setSearch] = useState("");
  const [filterDept, setFilterDept] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_EMP);
  const [showToast, ToastView] = useToast();

  function handleSave() {
    if (!form.name || !form.empId || !form.position || !form.department || !form.joinDate) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const newE = {
      id: `emp${Date.now()}`,
      empId: form.empId,
      name: form.name,
      position: form.position,
      department: form.department,
      phone: form.phone || "",
      email: form.email || "",
      joinDate: form.joinDate,
      salary: Number(form.salary) || 0,
      qualification: form.qualification || "—",
      nationality: "—",
      leaveBalance: 30,
      status: "active",
    };
    setEmployees(prev => [...prev, newE]);
    setForm(INITIAL_EMP);
    setShowAdd(false);
    showToast("تمت إضافة الموظف");
  }

  const depts = [...new Set(employees.map(e => e.department))];
  const filtered = employees.filter(e => {
    const matchSearch = e.name.includes(search) || e.position.includes(search) || e.empId.includes(search);
    const matchDept = filterDept === "all" || e.department === filterDept;
    return matchSearch && matchDept;
  });

  const EMP_STATUSES = [
    { key: "active", label: "فعّال", color: C.success },
    { key: "on_leave", label: "في إجازة", color: C.warning },
    { key: "resigned", label: "مستقيل", color: C.danger },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الموظفين" value={employees.length} icon={Users} color="#0284c7" />
        <KPICard title="فعّالين" value={employees.filter(e => e.status === "active").length} icon={UserCheck} color={C.success} />
        <KPICard title="في إجازة" value={employees.filter(e => e.status === "on_leave").length} icon={TreePine} color={C.warning} />
        <KPICard title="الأقسام" value={depts.length} icon={Building} color="#7c3aed" />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الرقم الوظيفي..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterDept} onChange={e => setFilterDept(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الأقسام</option>
              {depts.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة موظف</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "empId", label: "الرقم", width: "80px", render: v => <span style={{ fontWeight: 600, color: C.primary, fontFamily: "monospace" }}>{v}</span> },
            { key: "name", label: "الاسم", render: (v, row) => (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={styles.tableAvatar}>{v.charAt(0)}</div>
                <div>
                  <span style={{ fontWeight: 600, display: "block" }}>{v}</span>
                  <span style={{ fontSize: 11, color: C.textLight }}>{row.position}</span>
                </div>
              </div>
            )},
            { key: "department", label: "القسم", width: "120px" },
            { key: "joinDate", label: "تاريخ التعيين", width: "100px" },
            { key: "salary", label: "الراتب", width: "100px", render: v => `${v.toLocaleString()} ر` },
            { key: "phone", label: "الهاتف", width: "110px" },
            { key: "status", label: "الحالة", width: "90px", render: v => <StatusBadge status={v} statuses={EMP_STATUSES} /> },
          ]}
          data={filtered}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {selected && (
        <Modal title={`موظف — ${selected.name}`} onClose={() => setSelected(null)} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={styles.empHeader}>
              <div style={styles.empAvatarLg}>{selected.name.charAt(0)}</div>
              <div>
                <h3 style={{ margin: 0, color: C.primary }}>{selected.name}</h3>
                <p style={{ margin: "2px 0", color: C.teal, fontWeight: 600, fontSize: 14 }}>{selected.position}</p>
                <p style={{ margin: 0, color: C.textLight, fontSize: 12 }}>{selected.department} — {selected.empId}</p>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={styles.detailRow}><strong>الهاتف:</strong> <span dir="ltr">{selected.phone}</span></div>
              <div style={styles.detailRow}><strong>البريد:</strong> <span dir="ltr">{selected.email}</span></div>
              <div style={styles.detailRow}><strong>تاريخ التعيين:</strong> {selected.joinDate}</div>
              <div style={styles.detailRow}><strong>الراتب:</strong> {selected.salary.toLocaleString()} ريال</div>
              <div style={styles.detailRow}><strong>المؤهل:</strong> {selected.qualification}</div>
              <div style={styles.detailRow}><strong>الجنسية:</strong> {selected.nationality}</div>
              <div style={styles.detailRow}><strong>رصيد الإجازات:</strong> {selected.leaveBalance} يوم</div>
              <div style={styles.detailRow}><strong>الحالة:</strong> <StatusBadge status={selected.status} statuses={EMP_STATUSES} /></div>
            </div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة موظف جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_EMP); }} width={650}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الاسم الكامل" required placeholder="الاسم" value={form.name} onChange={v => setForm({ ...form, name: v })} />
              <Field label="الرقم الوظيفي" required placeholder="EMP-XXX" value={form.empId} onChange={v => setForm({ ...form, empId: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المنصب" required placeholder="المسمى الوظيفي" value={form.position} onChange={v => setForm({ ...form, position: v })} />
              <Field label="القسم" type="select" options={["الإدارة التنفيذية","الجاليات","المساجد","النسائي","المالية","تنمية الموارد","الاتصال","التطوع","الموارد البشرية"]} required value={form.department} onChange={v => setForm({ ...form, department: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="الهاتف" placeholder="05XXXXXXXX" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
              <Field label="البريد" placeholder="emp@bnr.org.sa" value={form.email} onChange={v => setForm({ ...form, email: v })} />
              <Field label="الراتب" type="number" placeholder="0" value={form.salary} onChange={v => setForm({ ...form, salary: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="المؤهل" type="select" options={["ثانوية","دبلوم","بكالوريوس","ماجستير","دكتوراه"]} value={form.qualification} onChange={v => setForm({ ...form, qualification: v })} />
              <Field label="تاريخ التعيين" type="date" required value={form.joinDate} onChange={v => setForm({ ...form, joinDate: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={UserPlus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_EMP); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Structure Tab — الهيكل التنظيمي
   ───────────────────────────────────────────── */
function StructureTab() {
  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 24 }}>
        <h4 style={styles.sectionTitle}><Layers size={18} color="#7c3aed" /> الهيكل التنظيمي للجمعية</h4>
        <div style={styles.orgChart}>
          {/* Level 1 */}
          <div style={styles.orgLevel}>
            <OrgNode name="مجلس الإدارة" role="الإشراف العام" color="#7c3aed" count={7} />
          </div>
          <div style={styles.orgLine} />
          {/* Level 2 */}
          <div style={styles.orgLevel}>
            <OrgNode name="م. أحمد الخالدي" role="المدير التنفيذي" color={C.primary} count={1} />
          </div>
          <div style={styles.orgLine} />
          {/* Level 3 */}
          <div style={{ ...styles.orgLevel, flexWrap: "wrap", gap: 10 }}>
            {ORG_DEPARTMENTS.map((d, i) => (
              <OrgNode key={i} name={d.manager} role={d.name} color={d.color} count={d.staff} />
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function OrgNode({ name, role, color, count }) {
  return (
    <div style={{ ...styles.orgNode, borderTop: `3px solid ${color}` }}>
      <div style={{ ...styles.orgNodeIcon, background: `${color}15` }}>
        <Users size={16} color={color} />
      </div>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.primary }}>{name}</p>
      <p style={{ margin: "2px 0", fontSize: 11, color: color, fontWeight: 600 }}>{role}</p>
      <span style={{ fontSize: 10, color: C.textLight }}>{count} موظف</span>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Leaves Tab — الإجازات
   ───────────────────────────────────────────── */
const LEAVE_TYPES = ["سنوية", "مرضية", "اضطرارية", "بدون راتب", "إذن خروج", "إذن تأخر"];
const INITIAL_LEAVE = {
  employee: "", type: "سنوية",
  from: "", to: "", fromHour: "", toHour: "",
  reason: "", replacementYes: "لا", replacementName: "",
  medicalAttachment: "", notes: "",
};

function LeavesTab({ canEdit, triggerOpen = 0 }) {
  const [leaves, setLeaves] = useState(DEMO_LEAVES);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_LEAVE);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const isPermission = form.type === "إذن خروج" || form.type === "إذن تأخر";
  const isSick = form.type === "مرضية";

  const days = (form.from && form.to)
    ? Math.max(1, Math.round((new Date(form.to) - new Date(form.from)) / (1000 * 60 * 60 * 24)) + 1)
    : 0;

  function handleSave() {
    if (!form.employee || !form.type || !form.from || !form.reason) {
      showToast("يرجى تعبئة الحقول المطلوبة (الموظف، النوع، تاريخ البداية، السبب)", "error");
      return;
    }
    if (!isPermission && !form.to) {
      showToast("تاريخ النهاية مطلوب", "error");
      return;
    }
    if (form.replacementYes === "نعم" && !form.replacementName) {
      showToast("يرجى تحديد اسم البديل", "error");
      return;
    }
    const calcDays = isPermission ? 0 : days;
    const newL = {
      id: `lv${Date.now()}`,
      employee: form.employee,
      type: form.type,
      from: form.from,
      to: form.to || form.from,
      fromHour: form.fromHour || "",
      toHour: form.toHour || "",
      days: calcDays,
      reason: form.reason || "—",
      replacementName: form.replacementYes === "نعم" ? form.replacementName : "",
      medicalAttachment: form.medicalAttachment || "",
      notes: form.notes || "",
      status: "pending",
    };
    setLeaves(prev => [newL, ...prev]);
    setForm(INITIAL_LEAVE);
    setShowAdd(false);
    showToast("تم إرسال طلب الإجازة / الإذن");
  }

  const LEAVE_STATUSES = [
    { key: "approved", label: "معتمدة", color: C.success },
    { key: "pending", label: "بانتظار الموافقة", color: C.warning },
    { key: "rejected", label: "مرفوضة", color: C.danger },
    { key: "active", label: "جارية", color: C.teal },
  ];

  const filtered = leaves.filter(l => filterStatus === "all" || l.status === filterStatus);

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="طلبات الإجازة" value={leaves.length} icon={TreePine} color="#059669" />
        <KPICard title="بانتظار الموافقة" value={leaves.filter(l => l.status === "pending").length} icon={Clock} color={C.warning} />
        <KPICard title="معتمدة" value={leaves.filter(l => l.status === "approved").length} icon={CheckCircle} color={C.success} />
        <KPICard title="جارية حالياً" value={leaves.filter(l => l.status === "active").length} icon={CalendarOff} color={C.teal} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
            <option value="all">جميع الحالات</option>
            {LEAVE_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">طلب إجازة</Btn>}
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "employee", label: "الموظف", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "type", label: "النوع", width: "100px" },
            { key: "from", label: "من", width: "100px" },
            { key: "to", label: "إلى", width: "100px" },
            { key: "days", label: "الأيام", width: "70px", render: v => <span style={{ fontWeight: 700, color: C.primary }}>{v}</span> },
            { key: "reason", label: "السبب" },
            { key: "status", label: "الحالة", width: "120px", render: v => <StatusBadge status={v} statuses={LEAVE_STATUSES} /> },
          ]}
          data={filtered}
        />
      </Card>

      {showAdd && (
        <Modal title="طلب إجازة / إذن" onClose={() => { setShowAdd(false); setForm(INITIAL_LEAVE); }} width={680}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الموظف" type="select" options={DEMO_EMPLOYEES.map(e => e.name)} required value={form.employee} onChange={v => setForm({ ...form, employee: v })} />
              <Field label="نوع الطلب" type="select" options={LEAVE_TYPES} required value={form.type} onChange={v => setForm({ ...form, type: v })} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="من تاريخ" type="date" required value={form.from} onChange={v => setForm({ ...form, from: v })} />
              <Field label={isPermission ? "إلى تاريخ" : "إلى تاريخ"} type="date" required={!isPermission} value={form.to} onChange={v => setForm({ ...form, to: v })} />
              <div>
                <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.textLight }}>عدد الأيام</label>
                <div style={{ padding: "10px 12px", background: C.bg, borderRadius: 8, fontWeight: 700, color: C.primary, textAlign: "center" }}>
                  {isPermission ? "—" : (days || 0)} يوم
                </div>
              </div>
            </div>

            {isPermission && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="من ساعة" type="time" value={form.fromHour} onChange={v => setForm({ ...form, fromHour: v })} />
                <Field label="إلى ساعة" type="time" value={form.toHour} onChange={v => setForm({ ...form, toHour: v })} />
              </div>
            )}

            <Field label="السبب" required type="textarea" placeholder="السبب التفصيلي للطلب..." rows={3} value={form.reason} onChange={v => setForm({ ...form, reason: v })} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
              <Field label="هل يوجد بديل؟" type="select" options={["لا", "نعم"]} value={form.replacementYes} onChange={v => setForm({ ...form, replacementYes: v })} />
              {form.replacementYes === "نعم" && (
                <Field label="اسم البديل" required type="select" options={DEMO_EMPLOYEES.map(e => e.name).filter(n => n !== form.employee)} value={form.replacementName} onChange={v => setForm({ ...form, replacementName: v })} />
              )}
            </div>

            {isSick && (
              <Field label="رفع تقرير طبي (رابط/اسم الملف)" placeholder="medical_report.pdf أو رابط الملف" value={form.medicalAttachment} onChange={v => setForm({ ...form, medicalAttachment: v })} />
            )}

            <Field label="ملاحظات إضافية" type="textarea" placeholder="ملاحظات اختيارية..." rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={TreePine} onClick={handleSave}>إرسال الطلب</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_LEAVE); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Payroll Tab — الرواتب
   ───────────────────────────────────────────── */
const PAYROLL_MONTHS = ["محرم", "صفر", "ربيع الأول", "ربيع الثاني", "جمادى الأولى", "جمادى الثانية", "رجب", "شعبان", "رمضان", "شوال", "ذو القعدة", "ذو الحجة"];
const PAYROLL_PAYMENT_METHODS = ["تحويل بنكي", "نقدي", "شيك"];
const INITIAL_PAYROLL = { month: "", year: "1447", totalAmount: "", paymentMethod: "تحويل بنكي", disbursementDate: "", notes: "" };

const INITIAL_ALLOWANCE = { employee: "", type: "بدل سكن", amount: "", month: "", notes: "" };
const ALLOWANCE_TYPES = ["بدل سكن", "بدل نقل", "بدل اتصال", "بدل طبيعة عمل", "مكافأة استثنائية", "بدل أخرى"];

function PayrollTab() {
  const totalSalary = DEMO_EMPLOYEES.reduce((s, e) => s + e.salary, 0);
  const [disbursements, setDisbursements] = useState(DEMO_DISBURSEMENTS);
  const [allowances, setAllowances] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showAllowance, setShowAllowance] = useState(false);
  const [form, setForm] = useState(INITIAL_PAYROLL);
  const [allowanceForm, setAllowanceForm] = useState(INITIAL_ALLOWANCE);
  const [showToast, ToastView] = useToast();

  function handleSaveDisbursement() {
    if (!form.month || !form.totalAmount) {
      showToast("الشهر والمبلغ مطلوبان", "error");
      return;
    }
    setDisbursements(prev => [{
      id: `d${Date.now()}`,
      month: form.month,
      year: form.year,
      totalAmount: Number(form.totalAmount) || 0,
      paymentMethod: form.paymentMethod,
      disbursementDate: form.disbursementDate || new Date().toISOString().slice(0, 10),
      notes: form.notes,
    }, ...prev]);
    setForm(INITIAL_PAYROLL);
    setShowAdd(false);
    showToast("تم تسجيل صرف الرواتب");
  }

  function handleSaveAllowance() {
    if (!allowanceForm.employee || !allowanceForm.amount) {
      showToast("الموظف والمبلغ مطلوبان", "error");
      return;
    }
    setAllowances(prev => [...prev, {
      id: `a${Date.now()}`,
      employee: allowanceForm.employee,
      type: allowanceForm.type,
      amount: Number(allowanceForm.amount) || 0,
      month: allowanceForm.month,
      notes: allowanceForm.notes,
    }]);
    setAllowanceForm(INITIAL_ALLOWANCE);
    setShowAllowance(false);
    showToast("تمت إضافة البدل");
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الرواتب الشهرية" value={`${(totalSalary / 1000).toFixed(0)}K`} icon={DollarSign} color={C.primary} />
        <KPICard title="عدد الموظفين" value={DEMO_EMPLOYEES.length} icon={Users} color="#0284c7" />
        <KPICard title="متوسط الراتب" value={`${(totalSalary / DEMO_EMPLOYEES.length / 1000).toFixed(1)}K`} icon={TrendingUp} color={C.teal} />
        <KPICard title="الرواتب السنوية" value={`${(totalSalary * 12 / 1000000).toFixed(1)}M`} icon={Calendar} color={C.accent} />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 16 }}>
        <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">صرف راتب شهري</Btn>
        <Btn icon={Plus} variant="ghost" onClick={() => setShowAllowance(true)} size="sm">إضافة بدل</Btn>
      </div>

      {/* Disbursements history */}
      {disbursements.length > 0 && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <h4 style={styles.sectionTitle}>سجل صرف الرواتب</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
            {disbursements.map(d => (
              <div key={d.id} style={{ padding: 12, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.primary }}>{d.month} {d.year}</p>
                <p style={{ margin: "4px 0 0", fontSize: 16, fontWeight: 700, color: C.success }}>{d.totalAmount.toLocaleString()} ر.س</p>
                <p style={{ margin: "4px 0 0", fontSize: 11, color: C.textLight }}>{d.paymentMethod} — {d.disbursementDate}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Allowances list */}
      {allowances.length > 0 && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <h4 style={styles.sectionTitle}>البدلات المضافة</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
            {allowances.map(a => (
              <div key={a.id} style={{ padding: 12, background: C.bg, borderRadius: 10, border: `1px solid ${C.border}` }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.primary }}>{a.employee}</p>
                <p style={{ margin: "2px 0", fontSize: 12, color: C.textLight }}>{a.type} — {a.month}</p>
                <p style={{ margin: "4px 0 0", fontSize: 15, fontWeight: 700, color: C.accent }}>{a.amount.toLocaleString()} ر.س</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "empId", label: "الرقم", width: "80px", render: v => <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{v}</span> },
            { key: "name", label: "الموظف", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "department", label: "القسم", width: "120px" },
            { key: "position", label: "المنصب", width: "140px" },
            { key: "salary", label: "الراتب الأساسي", width: "110px", render: v => `${v.toLocaleString()}` },
            { key: "allowances", label: "البدلات", width: "90px", render: (v, row) => `${Math.round(row.salary * 0.25).toLocaleString()}` },
            { key: "total", label: "الإجمالي", width: "110px", render: (v, row) => <span style={{ fontWeight: 700, color: C.success }}>{Math.round(row.salary * 1.25).toLocaleString()}</span> },
          ]}
          data={DEMO_EMPLOYEES.filter(e => e.status === "active")}
        />
      </Card>

      <div style={styles.totalBar}>
        <span>إجمالي الرواتب الشهرية (مع البدلات):</span>
        <span style={{ fontWeight: 700, color: C.primary, fontSize: 18 }}>{Math.round(totalSalary * 1.25).toLocaleString()} ريال</span>
      </div>

      {/* Department breakdown */}
      <Card style={{ padding: 20, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><Building size={18} color="#7c3aed" /> توزيع الرواتب حسب القسم</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
          {DEPT_PAYROLL.map((d, i) => (
            <div key={i} style={styles.payrollRow}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, width: 160 }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: C.primary }}>{d.name}</span>
              </div>
              <span style={{ fontSize: 13, color: C.textLight }}>{d.count} موظف</span>
              <div style={{ flex: 1 }}>
                <div style={styles.payrollBarBg}>
                  <div style={{ height: "100%", borderRadius: 4, width: `${(d.total / 35000) * 100}%`, background: d.color }} />
                </div>
              </div>
              <span style={{ fontSize: 14, fontWeight: 700, color: C.primary, minWidth: 90, textAlign: "left" }}>{d.total.toLocaleString()} ر</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Disbursement modal */}
      {showAdd && (
        <Modal title="صرف الرواتب الشهرية" onClose={() => { setShowAdd(false); setForm(INITIAL_PAYROLL); }} width={580}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="الشهر الهجري" type="select" options={PAYROLL_MONTHS} required value={form.month} onChange={v => setForm({ ...form, month: v })} />
              <Field label="السنة الهجرية" placeholder="1447" value={form.year} onChange={v => setForm({ ...form, year: v })} />
            </div>
            <Field label="المبلغ الإجمالي (ر.س)" type="number" required placeholder="0" value={form.totalAmount} onChange={v => setForm({ ...form, totalAmount: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="طريقة الدفع" type="select" options={PAYROLL_PAYMENT_METHODS} value={form.paymentMethod} onChange={v => setForm({ ...form, paymentMethod: v })} />
              <Field label="تاريخ الصرف" type="date" value={form.disbursementDate} onChange={v => setForm({ ...form, disbursementDate: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={DollarSign} onClick={handleSaveDisbursement}>تأكيد الصرف</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_PAYROLL); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Allowance modal */}
      {showAllowance && (
        <Modal title="إضافة بدل" onClose={() => { setShowAllowance(false); setAllowanceForm(INITIAL_ALLOWANCE); }} width={580}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="الموظف" type="select" options={DEMO_EMPLOYEES.map(e => e.name)} required value={allowanceForm.employee} onChange={v => setAllowanceForm({ ...allowanceForm, employee: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="نوع البدل" type="select" options={ALLOWANCE_TYPES} value={allowanceForm.type} onChange={v => setAllowanceForm({ ...allowanceForm, type: v })} />
              <Field label="المبلغ (ر.س)" type="number" required placeholder="0" value={allowanceForm.amount} onChange={v => setAllowanceForm({ ...allowanceForm, amount: v })} />
              <Field label="الشهر" type="select" options={PAYROLL_MONTHS} value={allowanceForm.month} onChange={v => setAllowanceForm({ ...allowanceForm, month: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" rows={2} placeholder="سبب البدل..." value={allowanceForm.notes} onChange={v => setAllowanceForm({ ...allowanceForm, notes: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={DollarSign} onClick={handleSaveAllowance}>حفظ البدل</Btn>
              <Btn variant="ghost" onClick={() => { setShowAllowance(false); setAllowanceForm(INITIAL_ALLOWANCE); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Demo Data
   ═════════════════════════════════════════════ */
const DEMO_DISBURSEMENTS = [
  { id: "d1", month: "محرم", year: "1447", totalAmount: 145000, paymentMethod: "تحويل بنكي", disbursementDate: "2026-01-28", notes: "" },
  { id: "d2", month: "صفر", year: "1447", totalAmount: 145000, paymentMethod: "تحويل بنكي", disbursementDate: "2026-02-28", notes: "" },
  { id: "d3", month: "ربيع الأول", year: "1447", totalAmount: 152000, paymentMethod: "تحويل بنكي", disbursementDate: "2026-03-28", notes: "تعيينات جديدة" },
];

const DEMO_EMPLOYEES = [
  { id: "emp1", empId: "EMP-001", name: "أحمد بن محمد الخالدي", position: "المدير التنفيذي", department: "الإدارة التنفيذية", phone: "0551001001", email: "ceo@bnr.org.sa", joinDate: "1435/01/01", salary: 18000, qualification: "ماجستير إدارة", nationality: "سعودي", leaveBalance: 30, status: "active" },
  { id: "emp2", empId: "EMP-002", name: "سارة العتيبي", position: "سكرتيرة المدير", department: "الإدارة التنفيذية", phone: "0552002002", email: "sec@bnr.org.sa", joinDate: "1438/03/15", salary: 9000, qualification: "بكالوريوس إدارة", nationality: "سعودية", leaveBalance: 22, status: "active" },
  { id: "emp3", empId: "EMP-003", name: "عبدالرحمن السبيعي", position: "مدير الجاليات", department: "الجاليات", phone: "0553003003", email: "comm@bnr.org.sa", joinDate: "1440/06/01", salary: 14000, qualification: "بكالوريوس شريعة", nationality: "سعودي", leaveBalance: 18, status: "active" },
  { id: "emp4", empId: "EMP-004", name: "يوسف الشمري", position: "مدير المساجد", department: "المساجد", phone: "0554004004", email: "mosques@bnr.org.sa", joinDate: "1439/09/10", salary: 13000, qualification: "بكالوريوس", nationality: "سعودي", leaveBalance: 20, status: "active" },
  { id: "emp5", empId: "EMP-005", name: "فهد الحربي", position: "المدير المالي", department: "المالية", phone: "0555005005", email: "fin@bnr.org.sa", joinDate: "1437/02/20", salary: 15000, qualification: "ماجستير محاسبة", nationality: "سعودي", leaveBalance: 25, status: "active" },
  { id: "emp6", empId: "EMP-006", name: "نورة الدوسري", position: "مديرة القسم النسائي", department: "النسائي", phone: "0556006006", email: "women@bnr.org.sa", joinDate: "1441/01/15", salary: 12000, qualification: "بكالوريوس تربية", nationality: "سعودية", leaveBalance: 16, status: "active" },
  { id: "emp7", empId: "EMP-007", name: "خالد المطيري", position: "مدير النظام", department: "تقنية المعلومات", phone: "0557007007", email: "it@bnr.org.sa", joinDate: "1442/04/01", salary: 11000, qualification: "بكالوريوس حاسب", nationality: "سعودي", leaveBalance: 14, status: "active" },
  { id: "emp8", empId: "EMP-008", name: "ماجد القرني", position: "محاسب", department: "المالية", phone: "0558008008", email: "acc@bnr.org.sa", joinDate: "1443/07/10", salary: 8500, qualification: "بكالوريوس محاسبة", nationality: "سعودي", leaveBalance: 12, status: "active" },
  { id: "emp9", empId: "EMP-009", name: "عمر البقمي", position: "منسق تطوع", department: "التطوع", phone: "0559009009", email: "vol@bnr.org.sa", joinDate: "1444/01/01", salary: 7500, qualification: "دبلوم", nationality: "سعودي", leaveBalance: 10, status: "on_leave" },
  { id: "emp10", empId: "EMP-010", name: "تركي الزهراني", position: "مصمم جرافيك", department: "الاتصال", phone: "0550100100", email: "design@bnr.org.sa", joinDate: "1444/06/15", salary: 8000, qualification: "بكالوريوس تصميم", nationality: "سعودي", leaveBalance: 8, status: "active" },
  { id: "emp11", empId: "EMP-011", name: "سلطان العسيري", position: "مدير تنمية الموارد", department: "تنمية الموارد", phone: "0551101101", email: "fund@bnr.org.sa", joinDate: "1441/08/01", salary: 13000, qualification: "بكالوريوس تسويق", nationality: "سعودي", leaveBalance: 19, status: "active" },
  { id: "emp12", empId: "EMP-012", name: "ناصر الغامدي", position: "مدير الموارد البشرية", department: "الموارد البشرية", phone: "0552202202", email: "hr@bnr.org.sa", joinDate: "1440/03/01", salary: 13500, qualification: "ماجستير موارد بشرية", nationality: "سعودي", leaveBalance: 21, status: "active" },
];

const ORG_DEPARTMENTS = [
  { name: "الجاليات", manager: "عبدالرحمن السبيعي", staff: 14, color: "#2563eb" },
  { name: "المساجد", manager: "يوسف الشمري", staff: 8, color: "#0891b2" },
  { name: "النسائي", manager: "نورة الدوسري", staff: 10, color: "#db2777" },
  { name: "المالية", manager: "فهد الحربي", staff: 4, color: "#16a34a" },
  { name: "تنمية الموارد", manager: "سلطان العسيري", staff: 5, color: "#d97706" },
  { name: "الاتصال", manager: "تركي الزهراني", staff: 3, color: "#c026d3" },
  { name: "التطوع", manager: "عمر البقمي", staff: 3, color: "#059669" },
  { name: "الموارد البشرية", manager: "ناصر الغامدي", staff: 2, color: "#0284c7" },
  { name: "تقنية المعلومات", manager: "خالد المطيري", staff: 2, color: "#475569" },
];

const DEMO_LEAVES = [
  { id: "lv1", employee: "عمر البقمي", type: "سنوية", from: "1446/09/01", to: "1446/09/15", days: 15, reason: "إجازة سنوية", status: "active" },
  { id: "lv2", employee: "أحمد الخالدي", type: "اضطرارية", from: "1446/09/20", to: "1446/09/22", days: 3, reason: "ظروف عائلية", status: "pending" },
  { id: "lv3", employee: "سارة العتيبي", type: "مرضية", from: "1446/08/25", to: "1446/08/28", days: 4, reason: "مراجعة طبية", status: "approved" },
  { id: "lv4", employee: "ماجد القرني", type: "سنوية", from: "1446/10/01", to: "1446/10/10", days: 10, reason: "إجازة سنوية", status: "pending" },
  { id: "lv5", employee: "تركي الزهراني", type: "سنوية", from: "1446/07/15", to: "1446/07/25", days: 10, reason: "سفر", status: "approved" },
  { id: "lv6", employee: "خالد المطيري", type: "مرضية", from: "1446/08/10", to: "1446/08/12", days: 3, reason: "مرض", status: "approved" },
  { id: "lv7", employee: "نورة الدوسري", type: "سنوية", from: "1446/11/01", to: "1446/11/15", days: 15, reason: "إجازة سنوية", status: "pending" },
];

const DEPT_PAYROLL = [
  { name: "الإدارة التنفيذية", count: 2, total: 27000, color: C.primary },
  { name: "المالية", count: 2, total: 23500, color: "#16a34a" },
  { name: "الجاليات", count: 1, total: 14000, color: "#2563eb" },
  { name: "الموارد البشرية", count: 1, total: 13500, color: "#0284c7" },
  { name: "المساجد", count: 1, total: 13000, color: "#0891b2" },
  { name: "تنمية الموارد", count: 1, total: 13000, color: "#d97706" },
  { name: "النسائي", count: 1, total: 12000, color: "#db2777" },
  { name: "تقنية المعلومات", count: 1, total: 11000, color: "#475569" },
  { name: "الاتصال", count: 1, total: 8000, color: "#c026d3" },
  { name: "التطوع", count: 1, total: 7500, color: "#059669" },
];

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16 },
  sectionTitle: { display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 700, color: C.primary, margin: "0 0 14px" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  searchBox: { display: "flex", alignItems: "center", gap: 8, background: C.bg, borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320 },
  searchInput: { border: "none", background: "transparent", outline: "none", fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%" },
  filterSelect: { padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer" },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },
  totalBar: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", marginTop: 12, background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, fontSize: 14, fontWeight: 500, color: C.primary },

  tableAvatar: {
    width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
    color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0,
  },
  empHeader: { display: "flex", alignItems: "center", gap: 20, paddingBottom: 14, borderBottom: `1px solid ${C.border}` },
  empAvatarLg: {
    width: 56, height: 56, borderRadius: 14, background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
    color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 22, flexShrink: 0,
  },

  /* Org Chart */
  orgChart: { display: "flex", flexDirection: "column", alignItems: "center", gap: 0, marginTop: 16 },
  orgLevel: { display: "flex", gap: 12, justifyContent: "center" },
  orgLine: { width: 2, height: 24, background: C.border, margin: "0 auto" },
  orgNode: {
    background: C.white, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px",
    textAlign: "center", minWidth: 130, boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  },
  orgNodeIcon: { width: 32, height: 32, borderRadius: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 6 },

  /* Payroll */
  payrollRow: { display: "flex", alignItems: "center", gap: 20 },
  payrollBarBg: { height: 8, borderRadius: 4, background: C.bg, overflow: "hidden" },
};
